// @flow
import React, { Component, Fragment } from "react";

import type { ErrorsType } from "../../types/UserFormType";
import { StyledTextField, StyledSelect } from "imagus-components";
import ChipInput from "../../../global/components/ChipInput";
import {
  ErrosDisplay,
  setErrorMsg,
  checkEmail,
  checkPhone,
  WarningDisplay
} from "../../containers/ErrosDisplay";
import { CancelButton, SubmitButton } from "imagus-components";
import { AusStates } from "../../../global/types/AusStates";
import { CompanyTypes } from "../../../global/types/CompanyTypes";
import {
  GraphqlLineBreak,
  TextareaMultilLines,
  utf8OutRangeFilter
} from "../../../global/functions/MultilLineStrings";
import CompanyBasicFields from "./CompanyBasicFields";

import Grid from "@material-ui/core/Grid";

const DEBOUNCETIME = 500;

type AssignLicenceFormStatesType = {
  errors: ErrorsType,
  loading: boolean,
  company_name: string,
  postcode: string,
  notes: string,
  authorised_reseller: boolean,
  abn: string,
  contact_name: string,
  contact_number: string,
  contact_email: string,
  country: string,
  state: string,
  other_state: string,
  city: string,
  suburb: string,
  address: string,
  company_type: string,
  group_name: string,
  tier_id: string,
  parent_id: string,
  parent_mismatch_warning: boolean,
  special_type: string,
  sheet_id: string
};

type CreateCompanyFormPropsType = {
  company: { [key: string]: * },
  createCompany: Function,
  updateCompany: Function,
  openSnackbar: Function,
  onClose: Function,
  isEdit?: boolean,
  isAdmin?: boolean,
  tiers?: Array<*>,
  companyList?: Array<*>,
  productSheets: Array<*>
};

const initialform: AssignLicenceFormStatesType = {
  errors: {},
  loading: false,
  company_name: "",
  postcode: "",
  notes: "",
  authorised_reseller: false,
  abn: "",
  contact_name: "",
  contact_number: "",
  contact_email: "",
  country: "Australia",
  state: "",
  other_state: "",
  city: "",
  suburb: "",
  address: "",
  company_type: "",
  group_name: "",
  tier_id: "",
  parent_id: "",
  parent_mismatch_warning: false,
  special_type: "",
  sheet_id: ""
};

class CreateCompanyForm extends Component<
  CreateCompanyFormPropsType,
  AssignLicenceFormStatesType
> {
  timeout: number;
  companyTypes: Array<*>;
  constructor(props: CreateCompanyFormPropsType) {
    super(props);
    this.submit = this.submit.bind(this);
    this.state = initialform;
    this.companyTypes = [];
  }

  componentDidMount() {
    const initial = { ...initialform };
    if (this.props.isEdit) {
      const company = this.props.company;
      initial.contact_name = company.contactName || "";
      initial.contact_number = company.contactNumber || "";
      initial.contact_email = company.contactEmail || "";
      initial.company_name = company.name || "";
      initial.abn = company.abn || "";
      initial.address = company.address || "";
      initial.suburb = company.suburb || "";
      initial.postcode = company.postcode || "";
      initial.city = company.city || "";
      const state = company.state || "";
      let index = AusStates.findIndex((x) => x.value === state);
      if (index < 0 && state !== "") {
        initial.state = "Other";
        initial.other_state = state;
      } else {
        initial.state = state;
      }
      initial.country = company.country || "";
      initial.notes = TextareaMultilLines(company.notes) || "";

      if (this.props.isAdmin) {
        this.companyTypes = CompanyTypes;

        let level = "";
        if (company.level === "D") {
          level = "DISTRIBUTER";
        } else if (company.level === "R") {
          level = "RESELLER";
        } else if (company.level === "E") {
          level = "ENDUSER";
        } else if (company.level === "I") {
          level = "TOPLEVEL";
        }
        initial.company_type = level;
        initial.authorised_reseller =
          company.authorisedReseller === -1 ? false : true;
        initial.group_name = company.groupName || "";
        initial.tier_id = company.tierId || "";
        initial.parent_id = company.parent ? company.parent.id : "";
        initial.special_type = company.specialType || "";
        initial.sheet_id = company.productSheetId || "";
      }
    } else {
      if (this.props.isAdmin) {
        if (this.props.company.level === "I") {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "DISTRIBUTER"
          );
          initial.company_type = "DISTRIBUTER";
        } else if (this.props.company.level === "D") {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "RESELLER" || row.value === "DISTRIBUTER"
          );
          initial.company_type = "RESELLER";
        } else if (this.props.company.level === "R") {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "ENDUSER" || row.value === "RESELLER"
          );
          initial.company_type = "ENDUSER";
        } else {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "ENDUSER"
          );
          initial.company_type = "ENDUSER";
        }

        initial.tier_id = this.props.tiers && this.props.tiers[0].value;
      } else {
        if (this.props.company.level === "I") {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "DISTRIBUTER"
          );
        } else if (this.props.company.level === "D") {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "RESELLER"
          );
        } else if (
          this.props.company.level === "R" ||
          this.props.company.level === "E"
        ) {
          this.companyTypes = CompanyTypes.filter(
            (row) => row.value === "ENDUSER"
          );
        }
        initial.company_type = this.companyTypes[0].value;
      }
    }
    this.setState({ ...initial, errors: {} });
  }

  handleTextChange = (e: Event & { currentTarget: HTMLInputElement }): * => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    this.setState({
      [name]: value
    });

    if (name === "contact_email") {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(
        function (val: string): * {
          this.setState({ errors: checkEmail(this.state.errors, val) });
        }.bind(this, value),
        DEBOUNCETIME
      );
    }

    if (name === "contact_number") {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(
        function (val: string) {
          this.setState({ errors: checkPhone(this.state.errors, val) });
        }.bind(this, value),
        DEBOUNCETIME
      );
    }
  };

  handleCheckBoxChange = (
    e: Event & { currentTarget: HTMLInputElement }
  ): * => {
    this.setState({
      [e.currentTarget.name]: !this.state[e.currentTarget.name]
    });
  };

  handleSelectChange = (e: SyntheticInputEvent<*>): * => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState({ [name]: value }, (): * => {
      if (name === "company_type") {
        this.checkCompanyTypeSelect();
      }
    });
  };

  syncChange = (name: string, value: Array<*>) => {
    this.setState(
      {
        [name]: value
      },
      (): * => {
        if (name === "parent_id") {
          let index =
            this.props.companyList &&
            this.props.companyList.findIndex((x) => x.value === value);
          const newParentType =
            index > -1 ? this.props.companyList[index].level : "";
          const oldType = this.props.company.parent
            ? this.props.company.parent.level
            : "";
          this.compareCompanyType(oldType, newParentType);
        }
      }
    );
  };

  checkCompanyTypeSelect = (): * => {
    if (this.state.company_type === "") {
      this.setState({
        errors: setErrorMsg(
          this.state.errors,
          "company",
          "Please Select a Company Type."
        )
      });
    } else {
      this.setState({
        errors: setErrorMsg(this.state.errors, "company", "", true)
      });
    }
  };

  compareCompanyType = (oldType: string, newType: string): * => {
    if (oldType !== newType) {
      this.setState({ parent_mismatch_warning: true });
    } else {
      this.setState({ parent_mismatch_warning: false });
    }
  };

  submit = async (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.props.isEdit) {
      this.checkCompanyTypeSelect();
    }
    if (Object.keys(this.state.errors).length === 0) {
      this.setState((): * => ({ loading: true }));

      const generalDetails = [
        this.state.contact_number.trim(),
        this.state.company_name,
        GraphqlLineBreak(utf8OutRangeFilter(this.state.notes)),
        this.state.abn,
        this.state.contact_email,
        this.state.postcode,
        this.state.contact_name,
        this.state.city,
        this.state.country,
        this.state.state === "Other"
          ? this.state.other_state
          : this.state.state,
        this.state.suburb,
        this.state.address,
        this.submitDone,
        this.submitFail
      ];

      const adminDetails = [
        this.state.authorised_reseller,
        this.state.company_type,
        this.state.group_name,
        this.state.tier_id,
        this.state.special_type,
        this.state.sheet_id
      ];

      if (this.props.isAdmin) {
        if (this.props.isEdit) {
          this.props.updateCompany(
            ...generalDetails,
            true,
            ...adminDetails,
            this.state.parent_id
          );
        } else {
          this.props.createCompany(...generalDetails, true, ...adminDetails);
        }
      } else {
        if (this.props.isEdit) {
          this.props.updateCompany(...generalDetails, false);
        } else {
          this.props.createCompany(
            ...generalDetails,
            false,
            this.state.authorised_reseller,
            this.state.company_type
          );
        }
      }
    }
  };

  submitDone = () => {
    this.props.onClose();
    this.props.openSnackbar(this.props.isEdit ? 4 : 1);
  };

  submitFail = (errors: ErrorsType) => {
    this.setState({ errors: errors, loading: false });
  };

  render(): React$Element<*> {
    const errors = this.state.errors;
    const loading = this.state.loading;

    return (
      <form
        onSubmit={(!loading && this.submit) || undefined}
        className="app-wrapper"
      >
        <div className="app-panel">
          <div className="app-panel-content">
            <Grid container spacing={8}>
              <CompanyBasicFields
                company_name={this.state.company_name}
                abn={this.state.abn}
                address={this.state.address}
                suburb={this.state.suburb}
                postcode={this.state.postcode}
                city={this.state.city}
                state={this.state.state}
                other_state={this.state.other_state}
                country={this.state.country}
                contact_name={this.state.contact_name}
                contact_email={this.state.contact_email}
                contact_number={this.state.contact_number}
                notes={this.state.notes}
                company_type={this.state.company_type}
                authorised_reseller={this.state.authorised_reseller}
                handleTextChange={this.handleTextChange}
                handleSelectChange={this.handleSelectChange}
                handleCheckBoxChange={this.handleCheckBoxChange}
                emailInvalid={errors.email === undefined ? false : true}
                phoneInvalid={errors.phone === undefined ? false : true}
                companyInvalid={errors.company === undefined ? false : true}
                hasCompanyType={!this.props.isEdit || this.props.isAdmin}
                companyTypes={this.companyTypes}
              />
              {this.props.isAdmin && (
                <Fragment>
                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      isRequired={false}
                      label="Group Name (only set this if this company is a branch)"
                      name="group_name"
                      value={this.state.group_name}
                      onChange={this.handleTextChange}
                      inpuType="text"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledSelect
                      value={this.state.tier_id}
                      onChange={this.handleSelectChange}
                      name="tier_id"
                      label="Select Tier"
                      isRequired={false}
                      menus={this.props.tiers}
                      isStyled
                    />
                  </Grid>
                  {this.props.isEdit && (
                    <Grid item xs={12}>
                      <ChipInput
                        name="parent_id"
                        label="Change Parent Company"
                        isStyled
                        placeholder="Select a parent company"
                        tags={this.props.companyList}
                        value={this.state.parent_id}
                        syncChange={this.syncChange}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      isRequired={false}
                      label="Sepcial Type(for Non-Standard workflow)"
                      name="special_type"
                      value={this.state.special_type}
                      onChange={this.handleTextChange}
                      inpuType="text"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ChipInput
                      value={this.state.sheet_id}
                      syncChange={this.syncChange}
                      name="sheet_id"
                      label="Select Product Sheet"
                      isRequired={false}
                      tags={this.props.productSheets}
                      placeholder="Select a Product Sheet"
                      isStyled
                    />
                  </Grid>
                </Fragment>
              )}
              <Grid item xs={12}>
                <WarningDisplay
                  warning="Parent company type changed, please check your current company type."
                  display={this.state.parent_mismatch_warning}
                />
              </Grid>
              <Grid item xs={12}>
                <ErrosDisplay errors={errors} form="create company" />
              </Grid>
              <Grid item xs={12}>
                <div className="form-right-btn-block">
                  <CancelButton onClick={this.props.onClose} />
                  <SubmitButton
                    loading={loading}
                    disabled={Object.keys(errors).length > 0}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </form>
    );
  }
}

export default CreateCompanyForm;
